import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Plus,
  DollarSign,
  Calendar,
  User,
  MoreVertical,
  Target,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const stages = [
  { key: 'qualification', label: 'Discovery', color: '#f97316' },
  { key: 'needs_analysis', label: 'Qualification', color: '#eab308' },
  { key: 'proposal', label: 'Proposal', color: '#14b8a6' },
  { key: 'negotiation', label: 'Negotiation', color: '#3b82f6' },
  { key: 'closed_won', label: 'Closed Won', color: '#22c55e' }
];

const normalizeStage = (stage) => String(stage || '').toLowerCase().replace(/\s+/g, '_');

export const Pipeline = () => {const { t } = useTranslation("crm");
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/opportunities`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch opportunities');
      const data = await response.json();
      setOpportunities(data);
    } catch (err) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const getOpportunitiesByStage = (stageKey) => {
    return opportunities.filter((opp) => normalizeStage(opp.stage) === stageKey);
  };

  const getTotalByStage = (stageKey) => {
    return getOpportunitiesByStage(stageKey).reduce((sum, opp) => sum + (opp.value || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="crm-pipeline">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("salesPipeline", "Sales Pipeline")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("visualOverviewOf", "Visual overview of your sales opportunities")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchOpportunities} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4" />{t("addOpportunity", "Add Opportunity")}

          </Button>
        </div>
      </div>

      {/* Pipeline Summary */}
      <div className="grid gap-4 sm:grid-cols-5">
        {stages.map((stage) =>
        <Card key={stage.key} className="card-elevated">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color }} />
                <span className="text-sm font-medium">{stage.label}</span>
              </div>
              <p className="text-2xl font-bold">${(getTotalByStage(stage.key) / 1000).toFixed(0)}k</p>
              <p className="text-xs text-muted-foreground">{getOpportunitiesByStage(stage.key).length}{t("opportunities", "opportunities")}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) =>
        <div key={stage.key} className="flex-shrink-0 w-80">
            <Card className="card-elevated h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color }} />
                    <CardTitle className="text-sm font-semibold">{stage.label}</CardTitle>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {getOpportunitiesByStage(stage.key).length}
                  </Badge>
                </div>
                <p className="text-lg font-bold text-muted-foreground">
                  ${(getTotalByStage(stage.key) / 1000).toFixed(0)}k
                </p>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
                {getOpportunitiesByStage(stage.key).map((opp) =>
              <Card key={opp.id} className="border shadow-sm hover:shadow-md transition-all cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{opp.name}</p>
                          <p className="text-xs text-muted-foreground">{opp.company}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6 -mr-2 -mt-1">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <DollarSign className="h-3.5 w-3.5" />
                            <span>{t("value", "Value")}</span>
                          </div>
                          <span className="text-sm font-semibold text-teal-600">
                            ${((opp.value || 0) / 1000).toFixed(0)}k
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Target className="h-3.5 w-3.5" />
                            <span>{t("probability", "Probability")}</span>
                          </div>
                          <span className="text-sm font-medium">{opp.probability}%</span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{t("closeDate", "Close Date")}</span>
                          </div>
                          <span className="text-xs">
                            {opp.expected_close ? new Date(opp.expected_close).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-teal-500/10 flex items-center justify-center">
                            <User className="h-3 w-3 text-teal-600" />
                          </div>
                          <span className="text-xs text-muted-foreground">{opp.owner}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
              )}
                
                {getOpportunitiesByStage(stage.key).length === 0 &&
              <div className="text-center py-8 text-muted-foreground">
                    <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t("noOpportunities", "No opportunities")}</p>
                  </div>
              }
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>);

};
